// Trailmap L2 Drip 2 — pure clustering helpers for the artifact grouping dims.
// No React / no ReactFlow runtime imports → safe in CRA-jest (api-free graph).
import type { Node } from 'reactflow';
import type { GroupingDim, ClusterBBox } from '../types';
import { CLUSTER_COLUMN_GAP } from '../chatDagTokens';

// Adopted verbatim from the locked L2 design §4.1 (single-arg form). Pathological
// values (empty, whitespace, "null"/"undefined") collapse to the null cluster.
export function normalizeClusterKey(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === '' || s === 'null' || s === 'undefined') return null;
  return s;
}

// Map the active dim to the artifact field it buckets on (L2 §4.1 table).
function dimRawValue(dim: GroupingDim, artifact: any): unknown {
  switch (dim) {
    case 'turn': return artifact?.source_turn_id;
    case 'phase': return artifact?.phase;
    case 'persona': return artifact?.agent_id;
    case 'off': default: return null;
  }
}

// Bucket artifact_tile nodes by their normalized dim key. The null key is the
// catch-all (no round / unknown persona / unbound turn).
export function clusterBy(
  tiles: Node[],
  dim: GroupingDim,
): Map<string | null, Node[]> {
  const out = new Map<string | null, Node[]>();
  for (const t of tiles) {
    const artifact = (t.data as any)?.artifact;
    const key = normalizeClusterKey(dimRawValue(dim, artifact));
    if (!out.has(key)) out.set(key, []);
    out.get(key)!.push(t);
  }
  return out;
}

const TILE_VGAP = 12;          // vertical gap between stacked tiles in a column
const BBOX_PAD_X = 16;
const BBOX_PAD_Y = 24;
const BBOX_HEADER_Y = 28;      // extra top room for the cluster header label

function nodeWH(n: Node): { w: number; h: number } {
  return { w: (n as any).width ?? 320, h: (n as any).height ?? 80 };
}

// Right edge of everything that STAYS on the canvas in grouped mode — spine +
// laterals + phase_stack boxes, but NOT the tiles (they relocate) or cluster
// boxes. The band anchors past this so columns never paint over the trail.
export function computeSpineRightEdge(nodes: Node[]): number {
  let edge = 0;
  for (const n of nodes) {
    if (n.type === 'artifact_tile' || n.type === 'cluster_aabb') continue;
    const { w } = nodeWH(n);
    edge = Math.max(edge, (n.position?.x ?? 0) + w);
  }
  return edge;
}

export interface PlacedCluster {
  key: string | null;
  dim: GroupingDim;
  value: string | null;
  count: number;
  bbox: ClusterBBox;
}

// Mutates each tile's position into deterministic L→R columns past spineRightEdge;
// returns one descriptor per cluster (with the AABB computed from TARGET positions).
// Sorted by key so layout is deterministic (null/catch-all sorts last).
export function placeColumns(
  clusters: Map<string | null, Node[]>,
  dim: GroupingDim,
  spineRightEdge: number,
): PlacedCluster[] {
  const sorted = [...clusters.entries()].sort((a, b) => {
    if (a[0] === b[0]) return 0;
    if (a[0] == null) return 1;       // catch-all last
    if (b[0] == null) return -1;
    return a[0].localeCompare(b[0]);
  });

  const out: PlacedCluster[] = [];
  let cursorX = spineRightEdge + CLUSTER_COLUMN_GAP;
  for (const [key, tiles] of sorted) {
    let y = 0;
    let colW = 0;
    for (const t of tiles) {
      const { w, h } = nodeWH(t);
      t.position = { x: cursorX, y };
      y += h + TILE_VGAP;
      colW = Math.max(colW, w);
    }
    out.push({
      key, dim, value: key, count: tiles.length,
      bbox: computeClusterBounds(tiles),
    });
    cursorX += colW + CLUSTER_COLUMN_GAP;
  }
  return out;
}

// Pure: AABB from the tiles' (already-assigned) target positions + explicit dims.
export function computeClusterBounds(tiles: Node[]): ClusterBBox {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const t of tiles) {
    const { w, h } = nodeWH(t);
    const x = t.position.x, yv = t.position.y;
    minX = Math.min(minX, x); minY = Math.min(minY, yv);
    maxX = Math.max(maxX, x + w); maxY = Math.max(maxY, yv + h);
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 0, h: 0 };
  return {
    x: minX - BBOX_PAD_X,
    y: minY - BBOX_PAD_Y - BBOX_HEADER_Y,
    w: maxX - minX + 2 * BBOX_PAD_X,
    h: maxY - minY + 2 * BBOX_PAD_Y + BBOX_HEADER_Y,
  };
}
